import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec2,
    vec3,
} from 'gl-matrix';
import { BoundingBox } from '../../data/common/BoundingBox';
import { RotatedBoundingBox } from '../../data/common/RotatedBoundingBox';

export class Rectangle3D {
    /**
     * Position of the center coordinates in patient space
     */
    protected _position: vec3;

    /**
     * Rotation from texture space to patient space
     */
    protected _rotation: mat3;

    /**
     * the scale in mm
     */
    protected _scale: vec2;

    /**
     * Transformation from texture space to patient space
     */
    protected _transformationMatrix: mat4;

    protected _invTransformationMatrix: mat4;

    /**
     *
     * @param position Position of the drawing plane's center coordinates in patient space
     * @param rotation Rotation of the drawing plane in patient space. Rotation from patient space to texture space
     * @param scale The scale of the drawing plane in millimeters
     */
    public constructor(
        position: ReadonlyVec3,
        rotation: ReadonlyMat3,
        scale: ReadonlyVec2
    ) {
        this._position = vec3.clone(position);
        this._rotation = mat3.clone(rotation);
        this._scale = vec2.clone(scale);
        this._transformationMatrix = mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.fromMat3(quat.create(), this._rotation),
            this._position,
            vec3.fromValues(this._scale[0], this._scale[1], 1)
        );
        this._invTransformationMatrix = mat4.invert(
            mat4.create(),
            this._transformationMatrix
        );
    }

    public get normalDirection() {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(0, 0, 1),
            this._rotation
        );
    }

    /**
     * Transforms point from global patient space to texture space coordinates.
     * The values within the texture space are in the range of [-1, 1].
     * @param patientSpaceCoordinates The patient space coordinates of the point that is to be converted
     */
    public toTextureSpace(patientSpaceCoordinates: ReadonlyVec3): vec2 {
        const textureSpaceCoordinates = vec3.transformMat4(
            vec3.create(),
            patientSpaceCoordinates,
            this._invTransformationMatrix
        );
        return vec2.fromValues(
            textureSpaceCoordinates[0],
            textureSpaceCoordinates[1]
        );
    }

    /**
     * Transforms point from textureSpace to Global patient space.
     * @param textureSpaceCoordinates
     * @returns
     */
    public toPatientSpace(textureSpaceCoordinates: ReadonlyVec2): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(
                textureSpaceCoordinates[0],
                textureSpaceCoordinates[1],
                0
            ),
            this._transformationMatrix
        );
    }

    /**
     * Position of the plane's center coordinates
     */
    public get position(): ReadonlyVec3 {
        return this._position;
    }

    /**
     * Rotation from patient space to texture space
     * The rotation of rectangle in PatientSpace
     */
    public get rotation(): ReadonlyMat3 {
        return this._rotation;
    }

    /**
     * This is half of the planar size of the {@link Rectangle3D} in millimeters.
     */
    public get scale(): ReadonlyVec2 {
        return this._scale;
    }

    /**
     * Transformation from ViewSpace to PatientSpace
     */
    public get transformationMatrix(): ReadonlyMat4 {
        return this._transformationMatrix;
    }

    /**
     * Transformation from PatientSpace to ViewSpace
     */
    public get invTransformationMatrix(): ReadonlyMat4 {
        return this._invTransformationMatrix;
    }

    /**
     * Returns a {@link RotatedBoundingBox} that is well aligned with this {@link Rectangle3D} and covers the whole area of this {@link Rectangle3D}.
     * @param normalDistance Margin in normal direction.
     * @returns A {@link RotatedBoundingBox} covering this rectangle.
     */
    public getBoundingBox(normalDistance: number): RotatedBoundingBox {
        const translation = vec3.transformMat3(
            vec3.create(),
            this._position,
            mat3.invert(mat3.create(), this._rotation)
        );
        const bb = new BoundingBox(translation);
        bb.addMargins(this.scale[0], this.scale[1], normalDistance);
        return new RotatedBoundingBox(bb, this._rotation);
    }

    public getVertices(): vec3[] {
        const vertices: vec3[] = [];
        const halfScaleX = this._scale[0] * 0.5;
        const halfScaleY = this._scale[1] * 0.5;
        const corners = [
            vec3.fromValues(-halfScaleX, -halfScaleY, 0),
            vec3.fromValues(halfScaleX, -halfScaleY, 0),
            vec3.fromValues(halfScaleX, halfScaleY, 0),
            vec3.fromValues(-halfScaleX, halfScaleY, 0),
        ];
        for (const corner of corners) {
            const vertex = vec3.transformMat4(
                vec3.create(),
                corner,
                this._transformationMatrix
            );
            vertices.push(vertex);
        }
        return vertices;
    }

    public get width(): number {
        return this._scale[0] * 2;
    }

    public get height(): number {
        return this._scale[1] * 2;
    }

    public get left(): number {
        const vertices = this.getVertices();
        return Math.min(...vertices.map((v) => v[0]));
    }

    public get top(): number {
        const vertices = this.getVertices();
        return Math.min(...vertices.map((v) => v[1]));
    }
}
